import { useState, useRef, useEffect } from 'react';

import { faPlay, faPause, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APIBaseChronos } from '../../api/hosts';
import useGetFetchConfig from '../../api/useGetFetchConfig';

interface AudioPlayerProps {
  blockId: string;
  caseId: string;
  transcriptId: string;
  className?: string;
  customColors?: {
    bg: string;
    bgHover: string;
    text: string;
    border?: string;
  };
  isProcessing?: boolean;
}

export default function AudioPlayer({ blockId, caseId, transcriptId, className = '', customColors, isProcessing = false }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const { fetchConfigGET } = useGetFetchConfig();

  const fetchAudioBlob = async () => {
    try {
      setIsLoading(true);

      const response = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/transcripts/${transcriptId}/blocks/${blockId}/audio`,
        fetchConfigGET,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      const audioBlob = await response.blob();
      return URL.createObjectURL(audioBlob);
    } catch (err) {
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // If no source is set, fetch the audio blob
        if (!audioRef.current.src) {
          const audioUrl = await fetchAudioBlob();
          audioRef.current.src = audioUrl;
        }

        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    return () => {
      if (audioRef.current?.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <div className={`flex items-center gap-2`}>
      <button
        onClick={handlePlayPause}
        disabled={isLoading || isProcessing}
        className={`flex items-center justify-center rounded-full transition-colors disabled:opacity-50 aspect-square ${className} ${
          customColors
            ? `${customColors.bg} ${customColors.text} ${customColors.bgHover} ${customColors.border || ''}`
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        }`}
        title={isProcessing ? 'Processing audio...' : (isPlaying ? 'Pause audio' : 'Play audio')}
      >
        {isLoading || isProcessing ? (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
        ) : (
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="text-xs" />
        )}
      </button>

      <audio ref={audioRef} onEnded={handleAudioEnded} preload="none" />
    </div>
  );
}
